import { useEffect, useRef, useState, useCallback } from 'react';

export default function CustomCursor() {
  const cursorRef = useRef<HTMLDivElement>(null);
  const trailRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const cleanupRef = useRef<(() => void)[]>([]);

  const growOnHover = useCallback(() => setIsHovering(true), []);
  const shrinkOnLeave = useCallback(() => setIsHovering(false), []);

  useEffect(() => {
    // Only show on desktop
    if (window.matchMedia('(pointer: fine)').matches) {
      setIsVisible(true);
    }

    const move = (e: MouseEvent) => {
      if (cursorRef.current) {
        cursorRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
      }
      if (trailRef.current) {
        trailRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
      }
    };

    document.addEventListener('mousemove', move);
    cleanupRef.current.push(() => document.removeEventListener('mousemove', move));

    // Add hover effects to interactive elements
    const addHoverListeners = () => {
      document.querySelectorAll('a, button, [role="button"]').forEach((el) => {
        el.addEventListener('mouseenter', growOnHover);
        el.addEventListener('mouseleave', shrinkOnLeave);
        cleanupRef.current.push(() => {
          el.removeEventListener('mouseenter', growOnHover);
          el.removeEventListener('mouseleave', shrinkOnLeave);
        });
      });
    };

    addHoverListeners();

    // Re-add listeners when DOM changes
    const observer = new MutationObserver(addHoverListeners);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      cleanupRef.current.forEach((cleanup) => cleanup());
      cleanupRef.current = [];
      observer.disconnect();
    };
  }, [growOnHover, shrinkOnLeave]);

  if (!isVisible) return null;

  return (
    <>
      <div
        ref={cursorRef}
        className="custom-cursor fixed top-0 left-0 z-50 pointer-events-none mix-blend-difference"
        style={{
          width: isHovering ? '40px' : '20px',
          height: isHovering ? '40px' : '20px',
          borderRadius: '50%',
          backgroundColor: 'white',
          transform: 'translate(-50%, -50%)',
          transition: 'width 0.2s cubic-bezier(0.23, 1, 0.32, 1), height 0.2s cubic-bezier(0.23, 1, 0.32, 1)',
          opacity: isHovering ? 0.8 : 1,
        }}
      />
      <div
        ref={trailRef}
        className="custom-cursor-trail fixed top-0 left-0 z-50 pointer-events-none"
        style={{
          width: isHovering ? '60px' : '40px',
          height: isHovering ? '60px' : '40px',
          borderRadius: '50%',
          border: '1px solid rgba(140, 82, 255, 0.3)',
          transform: 'translate(-50%, -50%)',
          transition: 'width 0.3s ease-out, height 0.3s ease-out, opacity 0.3s ease-out',
          opacity: isHovering ? 0.5 : 0.3,
        }}
      />
    </>
  );
}
